import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, Menu, Mail, FileText, Settings } from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../ui/tabs";
import AdminUsers from "./AdminUsers";
import AdminMenuControl from "./AdminMenuControl";
import AdminRoles from "./AdminRoles";
import AdminEmailLogs from "./AdminEmailLogs";
import AdminEmailSettings from "./AdminEmailSettings";
import DeveloperSettings from "../../pages/DeveloperSettings";
import AdminUiConfig from "./AdminUiConfig";
import { logger } from "../../lib/logger";
import { USER_PANEL_SUB_TABS as SUB_TABS } from "../../lib/tabs";

export default function AdminUsersPanel({ readOnly = false, subTabModes = {}, userRole, defaultTab, onTabChange }) {
  const [activeTab, setActiveTab] = useState(defaultTab || "users");
  const [saving, setSaving] = useState(false);

  const role = (userRole || "").trim().toLowerCase();
  const isDev = role === "developer" || role === "dev" || role === "programador";
  const isAdmin = role === "admin";

  // Sub-tabs filtering logic
  const visibleTabs = SUB_TABS.filter(t => {
    if (isDev) return true;
    if (t.value === "ui" || t.value === "menu" || t.value === "roles") {
      return isAdmin || subTabModes[`dev_${t.value}`];
    }
    return true;
  });

  return (
    <div data-id="admin-users-panel" className="max-w-6xl mx-auto pb-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 border-b border-fendi/20 pb-8">
        <div>
          <h2 className="font-serif text-4xl text-softblack mb-2 flex items-center gap-3">
            <Settings size={28} /> Administração
          </h2>
          <p className="text-warmgray text-sm">Utilizadores, permissões, conteúdo e configurações.</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (onTabChange) onTabChange(v); }} className="w-full">
        <div className="md:hidden overflow-x-auto no-scrollbar">
          <TabsList className="w-max min-w-full border-b border-fendi p-0 h-auto justify-start flex-nowrap rounded-none bg-transparent">
            {visibleTabs.map(({ value, icon: Icon, label }) => (
              <TabsTrigger key={value} value={value} className="relative shrink-0 px-5 py-3.5 data-[state=active]:text-softblack data-[state=active]:after:opacity-100 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-champagne after:opacity-0 transition-all rounded-none text-[10px] uppercase tracking-wider gap-2 text-warmgray hover:text-softblack">
                <Icon size={14} />
                <span>{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="flex gap-0 bg-white border border-fendi rounded-xl overflow-hidden shadow-sm">
          <TabsList className="hidden md:flex flex-col w-44 shrink-0 border-r border-fendi p-0 h-auto justify-start rounded-none bg-fendi/5">
            {visibleTabs.map(({ value, icon: Icon, label }) => (
              <TabsTrigger key={value} value={value} className="relative w-full px-5 py-3.5 data-[state=active]:bg-white data-[state=active]:text-softblack data-[state=active]:after:opacity-100 after:absolute after:right-0 after:top-2 after:bottom-2 after:w-0.5 after:bg-champagne after:opacity-0 transition-all rounded-none justify-start flex items-center gap-3 text-warmgray hover:text-softblack hover:bg-white/50">
                <Icon size={15} />
                <span className="text-[11px] uppercase tracking-wider">{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex-1 bg-white p-4 md:p-8">
            <TabsContent value="users" className="mt-0">
              <AdminUsers readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="menu" className="mt-0">
              <AdminMenuControl readOnly={readOnly} userRole={userRole} />
            </TabsContent>
            <TabsContent value="roles" className="mt-0">
              <AdminRoles readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="email" className="mt-0">
              <AdminEmailSettings readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="logs" className="mt-0">
              <AdminEmailLogs readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="ui" className="mt-0">
              <AdminUiConfig readOnly={readOnly} subTabModes={subTabModes} />
            </TabsContent>
            <TabsContent value="settings" className="mt-0">
              <DeveloperSettings readOnly={readOnly} />
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  );
}
